import { Controller } from "react-hook-form";
import { isEmail } from "class-validator";
import Link from "next/link";

import { useTranslation } from "@flare/i18n";
import { Checkbox, Input, Label, PhoneInput } from "@flare/ui/components";
import { parsePhoneNumber } from "@flare/ui/utils";

import { InputsContainer } from "../../components/inputs-container";
import { funnelInputsNameMap } from "../../config";

import { FormField } from "./form-field";

export function ContactInfoInputs() {
  const { t } = useTranslation();

  return (
    <>
      <InputsContainer className="mt-sp-4 space-y-sp-5 md:flex md:gap-sp-4 md:space-y-sp-0">
        <Controller
          name={funnelInputsNameMap.firstName}
          rules={{
            required: {
              value: true,
              message: t(
                "FUNNEL_CONTACT_INFO_FIRST_NAME_INPUT_VALIDATION_ERROR",
              ),
            },
          }}
          render={({ field, fieldState }) => (
            <FormField
              label={t("FUNNEL_CONTACT_INFO_FIRST_NAME_INPUT_LABEL")}
              field={(id) => (
                <Input
                  id={id}
                  placeholder={t(
                    "FUNNEL_CONTACT_INFO_FIRST_NAME_INPUT_PLACEHOLDER",
                  )}
                  hasError={!!fieldState.error}
                  {...field}
                />
              )}
              errorMessage={fieldState.error?.message}
            />
          )}
        />
        <Controller
          name={funnelInputsNameMap.lastName}
          rules={{
            required: {
              value: true,
              message: t(
                "FUNNEL_CONTACT_INFO_LAST_NAME_INPUT_VALIDATION_ERROR",
              ),
            },
          }}
          render={({ field, fieldState }) => (
            <FormField
              label={t("FUNNEL_CONTACT_INFO_LAST_NAME_INPUT_LABEL")}
              field={(id) => (
                <Input
                  id={id}
                  placeholder={t(
                    "FUNNEL_CONTACT_INFO_LAST_NAME_INPUT_PLACEHOLDER",
                  )}
                  hasError={!!fieldState.error}
                  {...field}
                />
              )}
              errorMessage={fieldState.error?.message}
            />
          )}
        />
      </InputsContainer>
      <InputsContainer className="mb-sp-8 mt-sp-5 space-y-sp-5 md:flex md:gap-sp-4 md:space-y-sp-0">
        <Controller
          name={funnelInputsNameMap.email}
          rules={{
            required: {
              value: true,
              message: t("FUNNEL_CONTACT_INFO_EMAIL_INPUT_VALIDATION_ERROR"),
            },
            validate: (_value) => {
              const value = _value?.trim();

              return (
                isEmail(value) ||
                t("FUNNEL_CONTACT_INFO_EMAIL_INPUT_VALIDATION_ERROR")
              );
            },
          }}
          render={({ field, fieldState }) => (
            <FormField
              label={t("FUNNEL_CONTACT_INFO_EMAIL_INPUT_LABEL")}
              field={(id) => (
                <Input
                  id={id}
                  placeholder={t("FUNNEL_CONTACT_INFO_EMAIL_INPUT_PLACEHOLDER")}
                  type="email"
                  hasError={!!fieldState.error}
                  {...field}
                />
              )}
              errorMessage={fieldState.error?.message}
            />
          )}
        />
        <Controller
          name={funnelInputsNameMap.phone}
          rules={{
            required: {
              value: true,
              message: t("FUNNEL_CONTACT_INFO_PHONE_INPUT_VALIDATION_ERROR"),
            },
            validate: (value) =>
              parsePhoneNumber(value).isValid ||
              t("FUNNEL_CONTACT_INFO_PHONE_INPUT_VALIDATION_ERROR"),
          }}
          render={({ field, fieldState }) => (
            <FormField
              label={t("FUNNEL_CONTACT_INFO_PHONE_INPUT_LABEL")}
              field={(id) => (
                <PhoneInput
                  id={id}
                  placeholder={t("FUNNEL_CONTACT_INFO_PHONE_INPUT_PLACEHOLDER")}
                  hasError={!!fieldState.error}
                  {...field}
                  value={field.value || ""}
                />
              )}
              errorMessage={fieldState.error?.message}
            />
          )}
        />
      </InputsContainer>
      <InputsContainer className="mb-sp-3">
        <Controller
          name={funnelInputsNameMap.approvedTerms}
          rules={{
            required: {
              value: true,
              message: t(
                "FUNNEL_CONTACT_INFO_TERMS_CHECK_BOX_VALIDATION_ERROR",
              ),
            },
          }}
          render={({ field, fieldState }) => (
            <div className="flex items-start gap-sp-2">
              <Checkbox
                id={funnelInputsNameMap.approvedTerms}
                required
                {...field}
                onCheckedChange={field.onChange}
                checked={field.value}
                hasError={!!fieldState.error}
              />

              <Label htmlFor={funnelInputsNameMap.approvedTerms}>
                {t("FUNNEL_CONTACT_INFO_TERMS_CHECK_BOX_LABEL_I_HAVE_READ")}{" "}
                <Link
                  target="_blank"
                  className="underline"
                  href="/terms-of-use"
                >
                  {t("TERMS_OF_USE")}
                </Link>{" "}
                {t("FUNNEL_CONTACT_INFO_TERMS_CHECK_BOX_LABEL_AND")}{" "}
                <Link target="_blank" className="underline" href="/privacy">
                  {t("PRIVACY_POLICY")}
                </Link>{" "}
                {t("FUNNEL_CONTACT_INFO_TERMS_CHECK_BOX_LABEL_CONSENT")}
              </Label>
            </div>
          )}
        />
      </InputsContainer>
    </>
  );
}
